import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, Lightbulb, Lock, MousePointerClick, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/common";
import { SupportButton } from "@/components/SupportDialog";
import { guiaParaTour } from "@/components/help/HelpAssistant";
import { api } from "@/lib/api";
import { startGuide } from "@/lib/tour";

const norm = (s) => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Central de ajuda: o manual completo do FSC, por assunto. Cada artigo tem o passo a passo, o botão para abrir a tela
// e, quando existe, "Mostrar onde clicar", que destaca na tela cada botão do passo a passo.
export default function Ajuda() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [aberto, setAberto] = useState(null);
  useEffect(() => { api.get("/help/manual").then((r) => setD(r.data.grupos)).catch(() => setD([])); }, []);
  const grupos = useMemo(() => {
    const t = norm(q).trim();
    if (!d || !t) return d;
    return d.map((g) => ({ ...g, artigos: g.artigos.filter((a) => norm(`${a.title} ${a.text} ${a.steps.join(" ")}`).includes(t)) }))
      .filter((g) => g.artigos.length);
  }, [d, q]);
  return (
    <div className="space-y-6" data-testid="ajuda-page">
      <PageHeader eyebrow="Central de ajuda" title="Manual do sistema" subtitle="Passo a passo de cada tela. Use “Mostrar onde clicar” para o sistema destacar cada botão." />
      <div className="relative max-w-xl">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar no manual: ex. abrir caixa, NFC-e, fechar o ponto" className="pl-9" data-testid="ajuda-busca" />
      </div>
      {!grupos && <p className="text-sm text-muted-foreground">Carregando o manual…</p>}
      {grupos && !grupos.length && (
        <div className="max-w-md space-y-2"><p className="text-sm text-muted-foreground">Nada encontrado no manual. Tente outras palavras ou fale com o suporte.</p><SupportButton /></div>)}
      {grupos?.map((g) => (
        <section key={g.grupo} className="space-y-2">
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold"><BookOpen className="h-5 w-5 text-primary" />{g.grupo}</h2>
          <div className="divide-y rounded-xl border bg-card">
            {g.artigos.map((a) => (
              <div key={a.id} className="px-4 py-3" data-testid={`ajuda-artigo-${a.id}`}>
                <button type="button" className="w-full text-left font-medium hover:text-primary" onClick={() => { if (aberto !== a.id) api.post("/help/evento", { tipo: "artigo_visto", artigo: a.id }).catch(() => {}); setAberto(aberto === a.id ? null : a.id); }}>{a.title}</button>
                {aberto === a.id && (
                  <div className="mt-2 space-y-3 text-sm">
                    <p className="leading-relaxed text-muted-foreground">{a.text}</p>
                    {a.steps.length > 0 && <ol className="ml-5 list-decimal space-y-1">{a.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>}
                    {a.tip && <p className="flex gap-2 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"><Lightbulb className="mt-0.5 h-3.5 w-3.5 shrink-0" />{a.tip}</p>}
                    {a.pode_abrir ? (
                      <div className="flex flex-wrap gap-2">
                        {a.guia?.length > 0 && <Button size="sm" onClick={() => startGuide(guiaParaTour(a), a.title, a.id)} data-testid="ajuda-guia-btn"><MousePointerClick className="mr-1 h-4 w-4" />Mostrar onde clicar</Button>}
                        {a.to && <Button size="sm" variant="outline" onClick={() => navigate(a.to)}>Abrir {a.label}<ArrowRight className="ml-1 h-4 w-4" /></Button>}
                      </div>
                    ) : a.to && <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5" />Seu usuário não tem acesso a esta tela. Fale com o administrador.</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
